import { useMemo } from 'react';
import { Autocomplete, Box, Chip, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AssetStatus, useGetGoodProfileListQuery } from '../../../graphql/generated';
import InfoBlock from '../../common/InfoBlock';
import { testIds } from "./ReceiverCommoditiesSection.testIds";

type Props = {
  commodityIds: string[];
  onChange: (ids: string[]) => void;
};

export default function ReceiverCommoditiesSection({ commodityIds, onChange }: Props) {
  const { t } = useTranslation('assets');
  const { data } = useGetGoodProfileListQuery({ includeInactive: true });
  const options = useMemo(
    () =>
      (data?.goodProfiles?.data ?? []).filter(
        (profile) =>
          profile.status !== AssetStatus.Inactive ||
          commodityIds.includes(profile._id)
      ),
    [data, commodityIds]
  );
  const value = useMemo(
    () => options.filter((gp) => commodityIds.includes(gp._id)),
    [options, commodityIds]
  );

  return (
    <InfoBlock title={t('receiverCommodities', 'Receiver Commodities')}>
      <Box px={2} pb={2}>
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          {t(
            'receiverCommoditiesHint',
            'Use this only if Tanks are not being tracked at a location. If Tanks are configured, they take priority.'
          )}
        </Typography>
        <Autocomplete data-testid={testIds.receiverCommoditiesSectionCommodityPicker}
          multiple
          options={options}
          value={value}
          getOptionLabel={(gp) => gp.label}
          isOptionEqualToValue={(opt, val) => opt._id === val._id}
          onChange={(_, newValue) => onChange(newValue.map((gp) => gp._id))}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                label={option.label}
                size="small"
                {...getTagProps({ index })}
                key={option._id}
              />
            ))
          }
          renderInput={(params) => <TextField {...params} size="small" inputProps={{ ...params.inputProps, "data-testid": testIds.receiverCommoditiesSectionCommoditySearch }} />}
        />
      </Box>
    </InfoBlock>
  );
}
